/**
 * Organization store: the user's organizations with their details, the active
 * organization and its members, its pending invitations (loaded on demand), and the
 * caller's role and permissions.
 *
 * Organizations, members and details come from the organizations tRPC router; every
 * membership and invitation mutation goes through Better Auth. Errors are translated per
 * transport — trpcErrorMessage for tRPC, authErrorMessage for Better Auth. Not persisted.
 */
import type {IMember} from '@models/member';
import type {IOrganization} from '@models/organization';
import type {IOrganizationDetails} from '@models/organization_details';
import type {OrgPermissions} from '@shared_ui/auth_client';
import {authClient} from '@shared_ui/auth_client';
import {authErrorMessage, isAuthError} from '@shared_ui/auth_errors';
import {trpcClient} from '@shared_ui/trpc_client';
import {buildTrpcErrorMessage} from '@shared_ui/trpc_errors';
import type {
   IMyInvitation,
   InvitationLookup,
   IOrgInvitation,
   SlugAvailability,
} from '@shared/auth_types';
import {InvitationStatus, OrgRole, toOrgRole} from '@shared/auth_types';
import {setAnalyticsOrganization} from '@src/plugins/analytics';
import {useAuthStore} from '@src/store/auth_store';
import {defineStore} from 'pinia';

export interface IOrgState {
   organizations: IOrganization[];
   activeOrg: IOrganization | null;
   members: IMember[];
   invitations: IOrgInvitation[];
   isLoaded: boolean;
   isLoadingMembers: boolean;

   /** Which user the loaded data belongs to (guards account switches). */
   loadedForUserId: string | null;
}

/** Organization-details fields a save may change; an absent key leaves the stored value alone. */
export type OrganizationDetailsPatch = Partial<
   Pick<IOrganizationDetails, 'description' | 'website' | 'contactEmail' | 'brandColor'>
>;

export const useOrgStore = defineStore('orgs', {
   state: (): IOrgState => ({
      organizations: [],
      activeOrg: null,
      members: [],
      invitations: [],
      isLoaded: false,
      isLoadingMembers: false,
      loadedForUserId: null,
   }),

   getters: {
      /** The caller's role in the active organization; null when not loaded. */
      myRole(state): OrgRole | null {
         const my_user_id = useAuthStore().user?.id;
         const my_member = state.activeOrg?.members?.find((member) => member.userId === my_user_id);
         return my_member == null ? null : toOrgRole(my_member.role);
      },

      /** The active organization's brand color; null until its details are loaded. */
      activeBrandColor(state): string | null {
         return state.activeOrg?.details?.brandColor ?? null;
      },

      isOwner(): boolean {
         return this.myRole === OrgRole.OWNER;
      },

      /** Whether the caller's role grants every listed permission; false when not loaded. */
      roleAllows(): (permissions: OrgPermissions) => boolean {
         return (permissions) => {
            const role = this.myRole;
            if (role == null) {
               return false;
            }
            return authClient.organization.checkRolePermission({role, permissions});
         };
      },

      canManageMembers(): boolean {
         return this.roleAllows({member: ['update', 'delete']});
      },

      canInvite(): boolean {
         return this.roleAllows({invitation: ['create', 'cancel']});
      },

      canUpdateOrg(): boolean {
         return this.roleAllows({organization: ['update']});
      },

      canDeleteOrg(): boolean {
         return this.roleAllows({organization: ['delete']});
      },

      pendingInvitations(state): IOrgInvitation[] {
         return state.invitations.filter((i) => i.status === InvitationStatus.PENDING);
      },

      /** Look up an active-organization member by member id; null when unknown or not loaded. */
      memberById(state): (member_id: string | null | undefined) => IMember | null {
         return (member_id) => {
            if (member_id == null) {
               return null;
            }
            return state.members.find((member) => member.id === member_id) ?? null;
         };
      },
   },

   actions: {
      // ---- [ Initialization and Loading ] ----- //
      /**
       * Load organization data for the signed-in user unless it is already
       * loaded for that user. Resets the store when signed out. Cheap to call repeatedly.
       */
      async ensureLoaded(): Promise<void> {
         const user_id = useAuthStore().user?.id ?? null;
         if (user_id == null) {
            this.$reset();
            return;
         }
         if (this.isLoaded && this.loadedForUserId === user_id) {
            return;
         }

         this.$reset();
         this.loadedForUserId = user_id;
         await this.initialize();
      },

      /**
       * Load the organizations and the active organization's members. Clears the
       * invitation list; loadInvitations() fills it on demand. isLoaded stays false when
       * the organization read fails, so the next trigger retries.
       */
      async initialize(): Promise<void> {
         this.invitations = [];
         const loaded = await this.loadOrganizations();
         await this.loadMembers();
         this.isLoaded = loaded;
      },

      /**
       * Load the caller's organizations with their details and set the active one from
       * the session. Returns false and clears both when the read fails.
       */
      async loadOrganizations(): Promise<boolean> {
         try {
            const result = await trpcClient().organizations.getMyOrganizations.query();
            this.organizations = result.organizations;
            this.activeOrg =
               result.organizations.find((org) => org.id === result.activeOrganizationId) ?? null;
            setAnalyticsOrganization(this.activeOrg?.id ?? null);
            return true;
         } catch (err) {
            console.warn('Could not load organizations', err);
            this.organizations = [];
            this.activeOrg = null;
            setAnalyticsOrganization(null);
            return false;
         }
      },

      /** Load the active organization's members with their user and details; empty on failure. */
      async loadMembers(): Promise<void> {
         const organization_id = this.activeOrg?.id;
         if (organization_id == null) {
            this.members = [];
            return;
         }

         this.isLoadingMembers = true;
         try {
            this.members = await trpcClient().organizations.listMembers.query({
               organizationId: organization_id,
            });
         } catch (err) {
            console.warn('Could not load organization members', err);
            this.members = [];
         } finally {
            this.isLoadingMembers = false;
         }
      },

      /** Switch the active organization. Returns an error message or null. */
      async setActive(organization_id: string): Promise<string | null> {
         const result = await authClient.organization.setActive({organizationId: organization_id});
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not switch organizations');
         }
         await this.initialize();
         return null;
      },

      // ------ [ Organization Management ] ----- //

      /**
       * Create an organization (creator becomes owner; the server also makes
       * it active). Returns an error message or null.
       */
      async createOrganization(opts: {name: string; slug: string}): Promise<string | null> {
         const result = await authClient.organization.create({
            name: opts.name.trim(),
            slug: opts.slug,
         });
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not create the organization');
         }

         await this.initialize();
         return null;
      },

      /**
       * Check whether a slug is free. 'invalid' means the check failed. The
       * server has no format rules, so normalize the slug before calling.
       */
      async checkSlugAvailable(slug: string): Promise<SlugAvailability> {
         const result = await authClient.organization.checkSlug({slug});

         if (result.error != null) {
            // checkSlug never returns {status: false} — taken arrives as an error.
            return isAuthError(result.error, 'ORGANIZATION_SLUG_ALREADY_TAKEN')
               ? 'taken'
               : 'invalid';
         }
         return 'available';
      },

      /**
       * Update the active organization's name and slug. Returns an error message or
       * null. An unchanged slug is fine to send; the server exempts the organization's
       * own slug from the uniqueness check.
       */
      async updateOrganization(opts: {name: string; slug: string}): Promise<string | null> {
         const result = await authClient.organization.update({
            data: {name: opts.name.trim(), slug: opts.slug},
         });
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not update the organization');
         }

         await this.loadOrganizations();
         return null;
      },

      /**
       * Write the given fields to the active organization's details. Returns an error
       * message or null.
       */
      async updateOrganizationDetails(patch: OrganizationDetailsPatch): Promise<string | null> {
         const organization_id = this.activeOrg?.id;
         if (organization_id == null) {
            return 'No active organization';
         }

         try {
            await trpcClient().organizations.updateDetails.mutate({
               organizationId: organization_id,
               ...patch,
            });
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not update the organization');
         }

         await this.loadOrganizations();
         return null;
      },

      /** Get the member count of any of the user's organizations; null when the lookup fails. */
      async fetchMemberCount(organization_id: string): Promise<number | null> {
         const result = await authClient.organization.listMembers({
            query: {organizationId: organization_id, limit: 1},
         });
         if (result.error != null) {
            return null;
         }
         return result.data?.total ?? null;
      },

      /** Pending-invitation count for any of the user's organizations;
       * null when the lookup fails. */
      async fetchPendingInvitationCount(organization_id: string): Promise<number | null> {
         const result = await authClient.organization.listInvitations({
            query: {organizationId: organization_id},
         });
         if (result.error != null) {
            return null;
         }
         return (result.data ?? []).filter((inv) => inv.status === InvitationStatus.PENDING).length;
      },

      /** Delete an organization (owner-only, server-enforced). */
      async deleteOrganization(organization_id: string): Promise<string | null> {
         const result = await authClient.organization.delete({organizationId: organization_id});
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not delete the organization');
         }

         await this.initialize();
         return null;
      },

      /** Leave an organization (last-owner protection is server-enforced). */
      async leaveOrganization(organization_id: string): Promise<string | null> {
         const result = await authClient.organization.leave({organizationId: organization_id});
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not leave the organization');
         }

         await this.initialize();
         return null;
      },

      // ------- [ Member Management ] ----- //

      /** Change a member's role (last-owner protection is server-enforced). */
      async updateMemberRole(member_id: string, role: OrgRole): Promise<string | null> {
         const my_user_id = useAuthStore().user?.id;
         const changed_myself = this.members.some(
            (member) => member.id === member_id && member.userId === my_user_id,
         );

         const result = await authClient.organization.updateMemberRole({
            memberId: member_id,
            role,
         });
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not change the role');
         }

         // The caller's own role lives on the organization listing, so reload it too.
         if (changed_myself) {
            await this.initialize();
         } else {
            await this.loadMembers();
         }
         return null;
      },

      /** Remove a member from the active organization. */
      async removeMember(member_id: string): Promise<string | null> {
         const result = await authClient.organization.removeMember({memberIdOrEmail: member_id});
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not remove the member');
         }

         await this.loadMembers();
         return null;
      },

      // ------- [ Invitation Management ] ----- //

      /** Load the active organization's pending invitations; empty on failure. */
      async loadInvitations(): Promise<void> {
         const result = await authClient.organization.listInvitations();
         if (result.error != null) {
            this.invitations = [];
            return;
         }

         const member_names = new Map(
            this.members.map((m) => [m.userId, m.user?.name ?? 'a member']),
         );
         this.invitations = (result.data ?? [])
            .map((inv) => toOrgInvitation(inv, member_names))
            .filter((inv) => inv.status === InvitationStatus.PENDING);
      },

      /** Invite (or re-invite with resend) an email to the active org. */
      async inviteMember(opts: {
         email: string;
         role: OrgRole;
         resend?: boolean;
      }): Promise<string | null> {
         const result = await authClient.organization.inviteMember({
            email: opts.email.trim(),
            role: opts.role,
            resend: opts.resend ?? false,
         });
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not send the invitation');
         }

         await this.loadInvitations();
         return null;
      },

      async cancelInvitation(invitation_id: string): Promise<string | null> {
         const result = await authClient.organization.cancelInvitation({
            invitationId: invitation_id,
         });
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not cancel the invitation');
         }

         await this.loadInvitations();
         return null;
      },

      /**
       * Get the pending invitations addressed to the signed-in user's email.
       * The server requires a verified email; otherwise the error message says so.
       */
      async loadMyInvitations(): Promise<{invitations: IMyInvitation[]} | {error: string}> {
         const result = await authClient.organization.listUserInvitations();

         if (result.error != null) {
            return {error: authErrorMessage(result.error, 'Could not load your invitations')};
         }

         const invitations = (result.data ?? []).map((inv) => ({
            id: inv.id,
            organizationId: inv.organizationId,
            organizationName: inv.organizationName ?? 'an organization',
            role: toOrgRole(inv.role ?? OrgRole.MEMBER),
            expiresAt: String(inv.expiresAt),
         }));
         return {invitations};
      },

      /**
       * Look up an invitation for the accept page. The server only shows an
       * invitation to a signed-in session whose email matches the invitee,
       * so "wrong account" is a distinct outcome from "not found".
       */
      async getInvitation(invitation_id: string): Promise<InvitationLookup> {
         const result = await authClient.organization.getInvitation({
            query: {id: invitation_id},
         });

         if (result.error != null) {
            return isAuthError(result.error, 'YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION')
               ? {kind: 'wrong-account'}
               : {kind: 'not-found'};
         }

         const inv = result.data;
         return {
            kind: 'found',
            invitation: {
               id: inv.id,
               email: inv.email,
               role: toOrgRole(inv.role),
               organizationName: inv.organizationName,
               inviterEmail: inv.inviterEmail,
               expiresAt: String(inv.expiresAt),
            },
         };
      },

      /** Accept an invitation (the server activates the new org). */
      async acceptInvitation(invitation_id: string): Promise<string | null> {
         const result = await authClient.organization.acceptInvitation({
            invitationId: invitation_id,
         });
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not accept the invitation');
         }

         await this.initialize();
         return null;
      },

      async rejectInvitation(invitation_id: string): Promise<string | null> {
         const result = await authClient.organization.rejectInvitation({
            invitationId: invitation_id,
         });
         if (result.error != null) {
            return authErrorMessage(result.error, 'Could not decline the invitation');
         }
         return null;
      },
   },
});

function toOrgInvitation(
   inv: {
      id: string;
      email: string;
      role?: string | null;
      status: string;
      expiresAt: Date | string;
      inviterId: string;
   },
   member_names: Map<string, string>,
): IOrgInvitation {
   return {
      id: inv.id,
      email: inv.email,
      role: toOrgRole(inv.role ?? OrgRole.MEMBER),
      status: toInvitationStatus(inv.status),
      expiresAt: String(inv.expiresAt),
      inviterName: member_names.get(inv.inviterId) ?? 'a member',
      isExpired: new Date(String(inv.expiresAt)).getTime() < Date.now(),
   };
}

function toInvitationStatus(status: string): InvitationStatus {
   const known = Object.values(InvitationStatus).find((s) => s === status);
   return known ?? InvitationStatus.PENDING;
}
